import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { bookingAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { CalendarIcon, ClockIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { ComingSoonPage } from '../../components/ComingSoon';

function BookingPage() {
  return (
    <ComingSoonPage
      feature="Book a Vehicle"
      description="Vehicle booking and reservation creation is currently under development."
      icon={<CalendarIcon className="h-10 w-10 text-gray-400" />}
    />
  );
}

/* --- Original implementation preserved below for re-enablement --- */
function _BookingPage_Original() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    vehicleId: 'test-vehicle-001', // Default to test vehicle
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '18:00'
  });
  const [quote, setQuote] = useState<any>(null);

  // Mock vehicles data (in real app, this would come from API)
  const vehicles = [
    {
      id: 'test-vehicle-001',
      make: 'Tesla',
      model: 'Model 3',
      year: 2023,
      image: '/api/placeholder/300/200',
      hourlyRate: '25.00',
      dailyRate: '200.00',
      features: ['Electric', 'Autopilot', 'Premium Interior'],
      location: 'Downtown Location'
    },
    {
      id: 'test-vehicle-002',
      make: 'BMW',
      model: 'i4',
      year: 2023,
      image: '/api/placeholder/300/200',
      hourlyRate: '30.00',
      dailyRate: '240.00',
      features: ['Electric', 'Sport Package', 'Heated Seats'],
      location: 'Airport Location'
    }
  ];

  const quoteMutation = useMutation(
    ({ vehicleId, startDate, endDate }: { vehicleId: string; startDate: string; endDate: string }) =>
      bookingAPI.getQuote(vehicleId, startDate, endDate),
    {
      onSuccess: (response) => {
        setQuote(response.data);
        setStep(3);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to get quote');
      }
    }
  );

  const reservationMutation = useMutation(
    bookingAPI.createReservation,
    {
      onSuccess: (response) => {
        toast.success('Reservation created successfully!');
        navigate(`/payment/${response.data.id}`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create reservation');
      }
    }
  );

  const handleDateTimeSubmit = () => {
    const startDateTime = `${bookingData.startDate}T${bookingData.startTime}:00`;
    const endDateTime = `${bookingData.endDate}T${bookingData.endTime}:00`;

    if (new Date(startDateTime) >= new Date(endDateTime)) {
      toast.error('End time must be after start time');
      return;
    }

    quoteMutation.mutate({
      vehicleId: bookingData.vehicleId,
      startDate: startDateTime,
      endDate: endDateTime
    });
  };

  const handleBooking = () => {
    const startDateTime = `${bookingData.startDate}T${bookingData.startTime}:00`;
    const endDateTime = `${bookingData.endDate}T${bookingData.endTime}:00`;

    reservationMutation.mutate({
      vehicleId: bookingData.vehicleId,
      startDate: startDateTime,
      endDate: endDateTime
    });
  };

  const selectedVehicle = vehicles.find(v => v.id === bookingData.vehicleId);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">Book a Vehicle</h1>
        <p className="mt-2 text-gray-600">Reserve your vehicle for the perfect ride</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center space-x-4">
          {[1, 2, 3].map((stepNum) => (
            <div key={stepNum} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= stepNum ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {stepNum}
              </div>
              {stepNum < 3 && (
                <div className={`h-1 w-16 ${step > stepNum ? 'bg-primary-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm text-gray-600">
          <span>Select Vehicle</span>
          <span>Choose Dates</span>
          <span>Confirm Booking</span>
        </div>
      </div>

      {/* Step 1: Vehicle Selection */}
      {step === 1 && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-on-surface">Choose Your Vehicle</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className={`card p-6 cursor-pointer transition-all ${
                  bookingData.vehicleId === vehicle.id
                    ? 'ring-2 ring-primary-600 bg-primary-50'
                    : 'hover:shadow-elevated'
                }`}
                onClick={() => setBookingData({ ...bookingData, vehicleId: vehicle.id })}
              >
                <div className="aspect-w-16 aspect-h-10 bg-gray-200 rounded-lg mb-4">
                  <div className="flex items-center justify-center text-gray-400">
                    🚗 {vehicle.make} {vehicle.model}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-on-surface">
                  {vehicle.make} {vehicle.model} {vehicle.year}
                </h3>
                <p className="text-gray-600 mb-3">{vehicle.location}</p>
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <span className="text-2xl font-bold text-on-surface">${vehicle.hourlyRate}</span>
                    <span className="text-gray-600">/hour</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-on-surface">${vehicle.dailyRate}</span>
                    <span className="text-gray-600">/day</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature) => (
                    <span
                      key={feature}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => setStep(2)}
              className="btn-primary"
              disabled={!bookingData.vehicleId}
            >
              Next: Choose Dates
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Date and Time Selection */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-on-surface">Select Dates & Times</h2>
            <button
              onClick={() => setStep(1)}
              className="btn-secondary"
            >
              Back
            </button>
          </div>

          <div className="card p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Start Date & Time */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-on-surface">Pickup</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      className="input-field"
                      value={bookingData.startDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      className="input-field"
                      value={bookingData.startTime}
                      onChange={(e) => setBookingData({ ...bookingData, startTime: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* End Date & Time */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-on-surface">Return</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      className="input-field"
                      value={bookingData.endDate}
                      min={bookingData.startDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      className="input-field"
                      value={bookingData.endTime}
                      onChange={(e) => setBookingData({ ...bookingData, endTime: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Vehicle Summary */}
            {selectedVehicle && (
              <div className="mt-6 p-4 bg-surface-container-low rounded-lg">
                <h4 className="font-medium text-on-surface mb-2">Selected Vehicle</h4>
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">🚗</div>
                  <div>
                    <p className="font-medium">{selectedVehicle.make} {selectedVehicle.model}</p>
                    <p className="text-sm text-gray-600">{selectedVehicle.location}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setStep(1)}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                onClick={handleDateTimeSubmit}
                disabled={!bookingData.startDate || !bookingData.endDate || quoteMutation.isLoading}
                className="btn-primary"
              >
                {quoteMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Get Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Quote and Confirmation */}
      {step === 3 && quote && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-on-surface">Booking Summary</h2>
            <button
              onClick={() => setStep(2)}
              className="btn-secondary"
            >
              Back
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Booking Details */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-4">Booking Details</h3>
              
              {selectedVehicle && (
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="text-3xl">🚗</div>
                    <div>
                      <h4 className="font-medium">{selectedVehicle.make} {selectedVehicle.model}</h4>
                      <p className="text-sm text-gray-600">{selectedVehicle.location}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium">Pickup</p>
                    <p className="text-sm text-gray-600">
                      {new Date(`${bookingData.startDate}T${bookingData.startTime}`).toLocaleDateString()} at {bookingData.startTime}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium">Return</p>
                    <p className="text-sm text-gray-600">
                      {new Date(`${bookingData.endDate}T${bookingData.endTime}`).toLocaleDateString()} at {bookingData.endTime}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-on-surface mb-4">Price Breakdown</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Base Price</span>
                  <span>${quote.basePrice}</span>
                </div>
                
                {quote.scarcitySurcharge && parseFloat(quote.scarcitySurcharge) > 0 && (
                  <div className="flex justify-between">
                    <span>Scarcity Surcharge</span>
                    <span>${quote.scarcitySurcharge}</span>
                  </div>
                )}
                
                {quote.weekendSurcharge && parseFloat(quote.weekendSurcharge) > 0 && (
                  <div className="flex justify-between">
                    <span>Weekend Surcharge</span>
                    <span>${quote.weekendSurcharge}</span>
                  </div>
                )}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>${quote.total}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleBooking}
                disabled={reservationMutation.isLoading}
                className="w-full btn-primary mt-6"
              >
                {reservationMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Book Now'}
              </button>
              
              <p className="text-xs text-gray-500 mt-2 text-center">
                You'll be redirected to payment after booking
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingPage;